import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { toast } from 'react-toastify';
import { getDownloadURL, ref, uploadString } from 'firebase/storage';
import { storage } from '../../firebase';
import {
  extFromDataUrl,
  getSlotImage,
  downloadDataUrl,
  MIN_VISUAL_SLOTS,
  VISUAL_SLOT_SECTIONS,
  getSectionSlotIndices
} from './campaignVisualSlotUtils';

const safeString = (value, fallback = '') => (typeof value === 'string' ? value : fallback);
const MAX_IMAGE_BYTES = 2.5 * 1024 * 1024;
const upBtnSize = 36;
const MAX_UPLOAD_DIMENSION = 1920;
const TARGET_DATA_URL_CHARS = 720000;

const loadImageElement = (dataUrl) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('IMAGE_DECODE_FAILED'));
    img.src = dataUrl;
  });

const encodeJpegFromDataUrl = async (dataUrl, maxDimension, quality) => {
  const img = await loadImageElement(dataUrl);
  const longest = Math.max(img.naturalWidth || img.width || 1, img.naturalHeight || img.height || 1);
  const scale = longest > maxDimension ? maxDimension / longest : 1;
  const width = Math.max(1, Math.round((img.naturalWidth || img.width || 1) * scale));
  const height = Math.max(1, Math.round((img.naturalHeight || img.height || 1) * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return dataUrl;
  ctx.drawImage(img, 0, 0, width, height);
  return canvas.toDataURL('image/jpeg', quality);
};

const compressImageDataUrl = async (dataUrl) => {
  // Firestore doküman limiti için kaliteyi kademeli düşür.
  let quality = 0.93;
  let out = await encodeJpegFromDataUrl(dataUrl, MAX_UPLOAD_DIMENSION, quality);
  while (out.length > TARGET_DATA_URL_CHARS && quality > 0.72) {
    quality -= 0.04;
    out = await encodeJpegFromDataUrl(dataUrl, MAX_UPLOAD_DIMENSION, quality);
  }
  return out;
};

const uploadCampaignImageToStorage = async (campaign, slot, dataUrl) => {
  const campaignId = safeString(campaign?.id, 'unknown-campaign');
  const now = Date.now();
  const path = `ukba_kasa/campaigns/${campaignId}/visual-slots/slot-${slot + 1}-${now}.jpg`;
  const imageRef = ref(storage, path);
  await uploadString(imageRef, dataUrl, 'data_url', {
    contentType: 'image/jpeg',
    cacheControl: 'public,max-age=31536000,immutable'
  });
  return getDownloadURL(imageRef);
};

/**
 * Kampanya görseli yükleme / indirme (3–20 alan, başlangıç 3; “Alan ekle” ile büyür).
 */
function CampaignVisualAssetsModal({ open, campaign, onClose, onSlotImageSet, onSlotIdeaNoteSet, onRemoveSlot }) {
  const fileInputRef = useRef(null);
  const pendingSlotRef = useRef(0);
  const [imagePreview, setImagePreview] = useState(null);
  const [dragOverSlot, setDragOverSlot] = useState(-1);
  const [activeSection, setActiveSection] = useState(0);
  const name = safeString(campaign?.name, 'kampanya');
  const slug = name
    .replace(/[^\w\-\sğüşıöçĞÜŞİÖÇ]/g, '')
    .replace(/\s+/g, '-')
    .slice(0, 48);

  const openFilePicker = (slotIndex) => {
    pendingSlotRef.current = slotIndex;
    fileInputRef.current?.click();
  };

  const handleSlotFile = (file, slot) => {
    if (!file || !campaign) return;
    if (!file.type || !file.type.startsWith('image/')) {
      toast.error('Lütfen bir görsel dosyası seçin', { autoClose: 2000 });
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error('Görsel çok büyük (en çok ~2,5 MB).', { autoClose: 3500 });
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result;
      if (typeof dataUrl !== 'string' || !onSlotImageSet) return;
      try {
        const optimized = await compressImageDataUrl(dataUrl);
        const storageUrl = await uploadCampaignImageToStorage(campaign, slot, optimized);
        onSlotImageSet(campaign, slot, storageUrl);
        toast.success('Görsel kaydedildi', { autoClose: 1200 });
      } catch (error) {
        if (error?.code === 'storage/unauthorized') {
          toast.error('Storage yetkisi yok. Storage kuralları güncellenmeli.', { autoClose: 3200 });
          return;
        }
        toast.error('Görsel işlenemedi', { autoClose: 2200 });
      }
    };
    reader.onerror = () => toast.error('Dosya okunamadı', { autoClose: 2000 });
    reader.readAsDataURL(file);
  };

  const onFileInputChange = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    handleSlotFile(file, pendingSlotRef.current);
  };

  if (!campaign) return null;

  return (
    <Dialog
      className="ky-visual-modal"
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        className: 'ky-visual-dialog-paper',
        sx: {
          bgcolor: '#fffaf0',
          border: '1px solid rgba(151,113,39,0.22)',
          borderRadius: 2,
          maxHeight: '92vh',
          minHeight: { xs: '56vh', sm: 'min(70vh, 720px)' },
          m: { xs: 1, sm: 2 }
        }
      }}
    >
      <DialogTitle sx={{ pr: 5, color: '#2f2516', fontWeight: 900, flexShrink: 0 }}>
        {name} — Kampanya görselleri
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: '#3b2b14' }}
          aria-label="Kapat"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          pb: 2.5,
          pt: 0,
          maxHeight: 'min(calc(92vh - 120px), 800px)',
          overflow: 'auto',
          pr: 0.5
        }}
      >
        <Typography sx={{ color: '#5f5140', fontSize: '0.88rem', mb: 1.1 }}>
          Her sekme mağaza içindeki ayrı kampanya alanını temsil eder. Görselleri ilgili alana göre yükleyin.
        </Typography>
        <Tabs
          value={activeSection}
          onChange={(_, next) => setActiveSection(next)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            mb: 1.1,
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 900, color: '#6b5b40', fontSize: '0.84rem', minHeight: 36, py: 0.4 },
            '& .Mui-selected': { color: '#7a4d0b !important' },
            '& .MuiTabs-indicator': { bgcolor: '#9a6a1f' }
          }}
        >
          {VISUAL_SLOT_SECTIONS.map((sec) => (
            <Tab key={sec.key} label={sec.label} />
          ))}
        </Tabs>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={onFileInputChange}
        />
        <Dialog
          open={!!imagePreview}
          onClose={() => setImagePreview(null)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { bgcolor: '#fffaf0', border: '1px solid rgba(151,113,39,0.2)', borderRadius: 2, position: 'relative', m: 2 }
          }}
        >
          <IconButton
            onClick={() => setImagePreview(null)}
            sx={{ position: 'absolute', right: 8, top: 8, zIndex: 1, color: '#3b2b14', bgcolor: 'rgba(255,255,255,0.9)', boxShadow: 1 }}
            aria-label="Kapat"
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <DialogContent sx={{ pt: 3, px: 2, pb: 2 }}>
            {imagePreview ? (
              <>
                <Typography sx={{ color: '#5f5140', fontSize: '0.82rem', fontWeight: 800, mb: 1, pr: 4 }}>
                  {name} — Görsel {imagePreview.slot}
                </Typography>
                <Box
                  component="img"
                  src={imagePreview.src}
                  alt=""
                  sx={{
                    width: '100%',
                    maxHeight: 'min(78vh, 640px)',
                    objectFit: 'contain',
                    borderRadius: 1,
                    border: '1px solid rgba(151,113,39,0.2)',
                    bgcolor: 'rgba(255,255,255,0.5)',
                    display: 'block',
                    boxSizing: 'border-box'
                  }}
                />
              </>
            ) : null}
          </DialogContent>
        </Dialog>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 0.9 }}>
          {VISUAL_SLOT_SECTIONS.slice(activeSection, activeSection + 1).flatMap((section, secIdx) =>
            getSectionSlotIndices(activeSection + secIdx).map((i, localIdx) => {
            const slotImg = getSlotImage(campaign, i);
            const slotListLen = Array.isArray(campaign?.visualSlotImages) ? campaign.visualSlotImages.length : 0;
            const canRemoveExtraSlot =
              typeof onRemoveSlot === 'function' &&
              i >= Math.max(MIN_VISUAL_SLOTS, VISUAL_SLOT_SECTIONS.length) &&
              slotListLen > Math.max(MIN_VISUAL_SLOTS, VISUAL_SLOT_SECTIONS.length);
            return (
              <Box
                key={`${section.key}-${i}`}
                className="ky-visual-slot-row"
                onDragOver={(event) => {
                  event.preventDefault();
                  if (dragOverSlot !== i) setDragOverSlot(i);
                }}
                onDragLeave={(event) => {
                  event.preventDefault();
                  if (!event.currentTarget.contains(event.relatedTarget)) {
                    setDragOverSlot(-1);
                  }
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  setDragOverSlot(-1);
                  const file = event.dataTransfer?.files?.[0];
                  handleSlotFile(file, i);
                }}
                sx={{
                  p: 0.9,
                  borderRadius: 1.5,
                  border: dragOverSlot === i ? '1px dashed rgba(22,101,52,0.7)' : '1px solid rgba(151,113,39,0.2)',
                  bgcolor: dragOverSlot === i ? 'rgba(34,197,94,0.08)' : 'rgba(255,255,255,0.55)',
                  display: 'grid',
                  gridTemplateColumns: '24px 82px 40px 96px auto auto',
                  alignItems: 'center',
                  gap: 0.95,
                  minWidth: 0
                }}
              >
                <Typography sx={{ minWidth: 24, fontWeight: 900, color: '#5f5140', flexShrink: 0 }}>{localIdx + 1}</Typography>
                <Typography sx={{ color: '#7a4d0b', fontSize: '0.78rem', fontWeight: 900, whiteSpace: 'nowrap' }}>Alan</Typography>
                <Tooltip title="Görsel yükle">
                  <span>
                    <IconButton
                      onClick={() => openFilePicker(i)}
                      size="small"
                      sx={{ width: upBtnSize, height: upBtnSize, color: '#7a4d0b', flexShrink: 0 }}
                      aria-label={`Yükle ${i + 1}`}
                    >
                      <CloudUploadIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                {slotImg ? (
                  <>
                    <Tooltip
                      title={
                        <Box>
                          <Typography
                            component="p"
                            variant="caption"
                            sx={{ color: 'rgba(44,40,32,0.9)', display: 'block', mb: 0.5, fontWeight: 800 }}
                          >
                            Önizleme — Tam boyut için tıklayın
                          </Typography>
                          <Box
                            component="img"
                            src={slotImg}
                            alt=""
                            sx={{ maxWidth: 220, maxHeight: 220, objectFit: 'contain', display: 'block', borderRadius: 1 }}
                          />
                        </Box>
                      }
                      enterDelay={200}
                      leaveDelay={100}
                      placement="right"
                      slotProps={{
                        popper: { sx: { zIndex: 1600 } },
                        tooltip: {
                          sx: {
                            p: 0.75,
                            maxWidth: 280,
                            bgcolor: 'rgba(255,250,240,0.98)',
                            border: '1px solid rgba(151,113,39,0.3)',
                            boxShadow: '0 10px 28px rgba(0,0,0,0.14)'
                          }
                        }
                      }}
                    >
                      <Box
                        component="span"
                        onClick={() => setImagePreview({ src: slotImg, slot: i + 1 })}
                        sx={{ lineHeight: 0, cursor: 'pointer', flexShrink: 0 }}
                      >
                        <Box
                          component="img"
                          src={slotImg}
                          alt=""
                          sx={{
                            width: 78,
                            height: 78,
                            objectFit: 'cover',
                            borderRadius: 1,
                            border: '1px solid rgba(151,113,39,0.3)',
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                            '&:hover': { transform: 'scale(1.08)', boxShadow: '0 4px 14px rgba(151,113,39,0.35)' }
                          }}
                        />
                      </Box>
                    </Tooltip>
                    <Tooltip title="İndir">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => {
                            const ext = extFromDataUrl(slotImg);
                            downloadDataUrl(slotImg, `UKBA-kampanya-${slug || 'gorsel'}-${section.key}-${localIdx + 1}.${ext}`);
                          }}
                          sx={{ color: '#5f5140', flexShrink: 0 }}
                        >
                          <DownloadIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    {onSlotImageSet ? (
                      <Tooltip title="Görseli kaldır">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => onSlotImageSet(campaign, i, null)}
                            sx={{ color: '#b91c1c', flexShrink: 0 }}
                            aria-label={`Görsel ${i + 1} kaldır`}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : null}
                  </>
                ) : (
                  <Typography sx={{ color: '#8a785f', fontSize: '0.9rem', flexShrink: 0, minWidth: 84 }}>Yükleme yok</Typography>
                )}
                {canRemoveExtraSlot ? (
                  <Tooltip title="Ek görsel alanını sil (satır gider)">
                    <span>
                      <Button
                        type="button"
                        size="small"
                        onClick={() => onRemoveSlot(campaign, i)}
                        sx={{ color: '#b91c1c', fontWeight: 800, textTransform: 'none', flexShrink: 0, whiteSpace: 'nowrap' }}
                      >
                        Alanı kaldır
                      </Button>
                    </span>
                  </Tooltip>
                ) : null}
                <Typography
                  sx={{
                    color: dragOverSlot === i ? '#166534' : '#8a785f',
                    fontSize: '0.74rem',
                    gridColumn: '1 / -1',
                    pl: { xs: 0, sm: 4.4 }
                  }}
                >
                  Görseli bu alana sürükleyip bırakabilir veya yükle ikonunu kullanabilirsiniz.
                </Typography>
              </Box>
            );
            })
          )}
        </Box>
        <Box sx={{ mt: 0.4 }}>
          <Typography sx={{ color: '#7a6b55', fontSize: '0.78rem' }}>
            Sekmeler: Ana Gorseller, Vitrin, Banko, Kapi Girisi, Banko Ustu, Kampanya Kosesi.
          </Typography>
        </Box>
      </DialogContent>
      <style>{`
        @media (max-width: 600px) {
          .ky-visual-dialog-paper {
            width: calc(100vw - 8px) !important;
            max-width: calc(100vw - 8px) !important;
            margin: 4px !important;
            max-height: calc(100svh - 8px) !important;
            min-height: calc(100svh - 8px) !important;
          }

          .ky-visual-slot-row {
            display: grid !important;
            grid-template-columns: 28px 1fr auto !important;
            grid-template-rows: auto auto auto auto !important;
            gap: 8px !important;
            align-items: start !important;
          }

          .ky-visual-slot-row > .MuiTypography-root:nth-of-type(1) {
            grid-column: 1 !important;
            grid-row: 1 !important;
          }

          .ky-visual-slot-row > .MuiTypography-root:nth-of-type(2) {
            grid-column: 2 / 4 !important;
            grid-row: 1 !important;
          }

          .ky-visual-slot-row > .MuiIconButton-root,
          .ky-visual-slot-row > span:first-of-type {
            grid-column: 3 !important;
            grid-row: 1 !important;
          }

          .ky-visual-slot-row > span,
          .ky-visual-slot-row > .MuiTypography-root:last-of-type {
            grid-column: 1 / -1 !important;
          }

          .ky-visual-slot-row img {
            width: 72px !important;
            height: 72px !important;
          }

          .ky-visual-slot-row .MuiButton-root {
            width: 100% !important;
            grid-column: 1 / -1 !important;
          }
        }
      `}</style>
    </Dialog>
  );
}

export default CampaignVisualAssetsModal;
